import { isSVGElement } from '@adflow/types';
import type { AdElement } from '@adflow/types';

import type { Point } from './viewport';

/**
 * The compiled SVG layer can be selected alone but never as part of a multi-selection.
 * Single source of truth for the rule: used by the store and the canvas session. It keys off
 * the element type (not hit-target membership) so hidden or otherwise non-hittable layers
 * stay in a group.
 */
export function withoutCompiledSvgInMultiSelection(
  elements: readonly AdElement[],
  ids: string[],
): string[] {
  if (ids.length < 2) return ids;
  const svgIds = new Set(elements.filter(isSVGElement).map((el) => el.id));
  return ids.filter((id) => !svgIds.has(id));
}

export type AxisAlignedBounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type HitTarget = {
  id: string;
  z: number;
  bounds: AxisAlignedBounds;
  locked?: boolean | null;
  hidden?: boolean | null;
};

/** When z ties, higher array index wins (stable, documented tie-break). */
export function pickTopmostAtPoint(
  targets: readonly HitTarget[],
  point: Point,
  options?: { includeLocked?: boolean; includeHidden?: boolean },
): string | null {
  const includeLocked = options?.includeLocked ?? false;
  const includeHidden = options?.includeHidden ?? false;

  const hits = targets
    .map((target, index) => ({ target, index }))
    .filter(({ target }) => {
      if (!includeLocked && target.locked === true) return false;
      if (!includeHidden && target.hidden === true) return false;
      return pointInBounds(point, target.bounds);
    });

  if (hits.length === 0) {
    return null;
  }

  hits.sort((a, b) => {
    if (b.target.z !== a.target.z) {
      return b.target.z - a.target.z;
    }
    return b.index - a.index;
  });

  return hits[0]?.target.id ?? null;
}

/** Axis-aligned rectangle spanned by two corner points, in any drag direction. */
export function normalizeRect(a: Point, b: Point): AxisAlignedBounds {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    width: Math.abs(a.x - b.x),
    height: Math.abs(a.y - b.y),
  };
}

/** Edges that touch count as intersecting. */
export function boundsIntersect(a: AxisAlignedBounds, b: AxisAlignedBounds): boolean {
  return (
    a.x <= b.x + b.width && a.x + a.width >= b.x && a.y <= b.y + b.height && a.y + a.height >= b.y
  );
}

/**
 * Ids of every target the marquee rectangle intersects, in target order.
 * Locked and hidden targets are skipped unless explicitly included.
 */
export function pickTargetsInRect(
  targets: readonly HitTarget[],
  rect: AxisAlignedBounds,
  options?: { includeLocked?: boolean; includeHidden?: boolean },
): string[] {
  const includeLocked = options?.includeLocked ?? false;
  const includeHidden = options?.includeHidden ?? false;

  return targets
    .filter((target) => {
      if (!includeLocked && target.locked === true) return false;
      if (!includeHidden && target.hidden === true) return false;
      return boundsIntersect(rect, target.bounds);
    })
    .map((target) => target.id);
}

export function pointInBounds(point: Point, bounds: AxisAlignedBounds): boolean {
  return (
    point.x >= bounds.x &&
    point.x <= bounds.x + bounds.width &&
    point.y >= bounds.y &&
    point.y <= bounds.y + bounds.height
  );
}

export function combinedAxisAlignedBounds(
  targets: readonly HitTarget[],
  ids: readonly string[],
): AxisAlignedBounds | null {
  const selected = targets.filter((target) => ids.includes(target.id));
  if (selected.length === 0) {
    return null;
  }

  const xs = selected.map((t) => t.bounds.x);
  const ys = selected.map((t) => t.bounds.y);
  const rights = selected.map((t) => t.bounds.x + t.bounds.width);
  const bottoms = selected.map((t) => t.bounds.y + t.bounds.height);

  const x = Math.min(...xs);
  const y = Math.min(...ys);
  const right = Math.max(...rights);
  const bottom = Math.max(...bottoms);

  return { x, y, width: right - x, height: bottom - y };
}
